import React from 'react';
import { useNavigate } from 'react-router-dom';
import { createPageUrl } from '../../../utils';
import {
  Users,
  MoreVertical,
  Edit,
  Trash2,
  Eye,
  FileText,
  CheckCircle,
  XCircle,
  AlertTriangle,
} from 'lucide-react';

interface Debtor {
  id: number;
  full_name: string;
  email?: string;
  cif?: string;
  id_type?: string;
  id_number?: string;
  phone?: string;
  slik_status?: string;
  document_status?: string;
  status?: string;
}

interface DebtorTableProps {
  debtors: Debtor[];
  loading: boolean;
  onDelete: (id: number) => void;
}

const statusColors: Record<string, string> = {
  active: 'bg-green-100 text-green-700',
  inactive: 'bg-gray-100 text-gray-700',
  blocked: 'bg-red-100 text-red-700',
};

const docStatusColors: Record<string, string> = {
  complete: 'bg-green-100 text-green-700',
  incomplete: 'bg-yellow-100 text-yellow-700',
  expired: 'bg-red-100 text-red-700',
};

const docStatusIcons: Record<string, React.ComponentType<{ className?: string }>> = {
  complete: CheckCircle,
  incomplete: AlertTriangle,
  expired: XCircle,
};

export default function DebtorTable({ debtors, loading, onDelete }: DebtorTableProps) {
  const navigate = useNavigate();
  const [openMenu, setOpenMenu] = React.useState<number | null>(null);

  return (
    <div className="bg-white rounded-lg border shadow-sm">
      <div className="overflow-x-auto">
        <table className="w-full">
          <thead>
            <tr className="bg-gray-50 border-b">
              <th className="text-left px-4 py-3 text-sm font-medium text-gray-600">Debitur</th>
              <th className="text-left px-4 py-3 text-sm font-medium text-gray-600">CIF</th>
              <th className="text-left px-4 py-3 text-sm font-medium text-gray-600">
                No. Identitas
              </th>
              <th className="text-left px-4 py-3 text-sm font-medium text-gray-600">Telepon</th>
              <th className="text-left px-4 py-3 text-sm font-medium text-gray-600">SLIK</th>
              <th className="text-left px-4 py-3 text-sm font-medium text-gray-600">Dokumen</th>
              <th className="text-left px-4 py-3 text-sm font-medium text-gray-600">Status</th>
              <th className="w-12 px-4 py-3"></th>
            </tr>
          </thead>
          <tbody>
            {loading ? (
              Array(5)
                .fill(0)
                .map((_, i) => (
                  <tr key={i} className="border-b">
                    <td className="px-4 py-3">
                      <div className="h-10 w-48 bg-gray-200 rounded animate-pulse" />
                    </td>
                    <td className="px-4 py-3">
                      <div className="h-4 w-24 bg-gray-200 rounded animate-pulse" />
                    </td>
                    <td className="px-4 py-3">
                      <div className="h-4 w-32 bg-gray-200 rounded animate-pulse" />
                    </td>
                    <td className="px-4 py-3">
                      <div className="h-4 w-28 bg-gray-200 rounded animate-pulse" />
                    </td>
                    <td className="px-4 py-3">
                      <div className="h-6 w-20 bg-gray-200 rounded animate-pulse" />
                    </td>
                    <td className="px-4 py-3">
                      <div className="h-6 w-24 bg-gray-200 rounded animate-pulse" />
                    </td>
                    <td className="px-4 py-3">
                      <div className="h-6 w-20 bg-gray-200 rounded animate-pulse" />
                    </td>
                    <td className="px-4 py-3">
                      <div className="h-8 w-8 bg-gray-200 rounded animate-pulse" />
                    </td>
                  </tr>
                ))
            ) : debtors.length === 0 ? (
              <tr>
                <td colSpan={8} className="text-center py-12">
                  <Users className="w-12 h-12 mx-auto mb-3 text-gray-300" />
                  <p className="text-gray-500">Tidak ada data debitur</p>
                </td>
              </tr>
            ) : (
              debtors.map(debtor => {
                const DocIcon = docStatusIcons[debtor.document_status || ''] || AlertTriangle;
                return (
                  <tr key={debtor.id} className="border-b hover:bg-gray-50">
                    <td className="px-4 py-3">
                      <div className="flex items-center gap-3">
                        <div className="w-10 h-10 rounded-full bg-amber-100 flex items-center justify-center">
                          <span className="font-semibold text-amber-700">
                            {debtor.full_name?.charAt(0) || 'D'}
                          </span>
                        </div>
                        <div>
                          <p className="font-medium">{debtor.full_name}</p>
                          <p className="text-sm text-gray-500">{debtor.email || '-'}</p>
                        </div>
                      </div>
                    </td>
                    <td className="px-4 py-3 font-mono text-sm">{debtor.cif || '-'}</td>
                    <td className="px-4 py-3 text-sm">
                      <span className="text-gray-500">{debtor.id_type?.toUpperCase()}: </span>
                      {debtor.id_number}
                    </td>
                    <td className="px-4 py-3">{debtor.phone || '-'}</td>
                    <td className="px-4 py-3">
                      <span
                        className={`px-2 py-1 rounded-full text-xs font-medium ${
                          debtor.slik_status === 'clean'
                            ? 'bg-green-100 text-green-700'
                            : debtor.slik_status === 'watchlist'
                              ? 'bg-yellow-100 text-yellow-700'
                              : debtor.slik_status === 'blacklist'
                                ? 'bg-red-100 text-red-700'
                                : 'bg-gray-100 text-gray-700'
                        }`}
                      >
                        {debtor.slik_status === 'clean'
                          ? 'Bersih'
                          : debtor.slik_status === 'watchlist'
                            ? 'Waspada'
                            : debtor.slik_status === 'blacklist'
                              ? 'Blacklist'
                              : '-'}
                      </span>
                    </td>
                    <td className="px-4 py-3">
                      <span
                        className={`px-2 py-1 rounded-full text-xs font-medium flex items-center gap-1 w-fit ${docStatusColors[debtor.document_status || ''] || 'bg-gray-100'}`}
                      >
                        <DocIcon className="w-3 h-3" />
                        {debtor.document_status === 'complete'
                          ? 'Lengkap'
                          : debtor.document_status === 'expired'
                            ? 'Kadaluarsa'
                            : 'Tidak Lengkap'}
                      </span>
                    </td>
                    <td className="px-4 py-3">
                      <span
                        className={`px-2 py-1 rounded-full text-xs font-medium ${statusColors[debtor.status || ''] || statusColors.active}`}
                      >
                        {debtor.status === 'active'
                          ? 'Aktif'
                          : debtor.status === 'inactive'
                            ? 'Tidak Aktif'
                            : 'Diblokir'}
                      </span>
                    </td>
                    <td className="px-4 py-3 relative">
                      <button
                        className="p-2 hover:bg-gray-100 rounded-lg"
                        onClick={() => setOpenMenu(openMenu === debtor.id ? null : debtor.id)}
                      >
                        <MoreVertical className="w-4 h-4" />
                      </button>
                      {openMenu === debtor.id && (
                        <div className="absolute right-0 mt-2 w-48 bg-white rounded-lg shadow-lg border z-50">
                          <button
                            className="w-full px-4 py-2 text-left hover:bg-gray-50 flex items-center gap-2 text-sm"
                            onClick={() => {
                              navigate(createPageUrl(`DebtorDetail?id=${debtor.id}`));
                              setOpenMenu(null);
                            }}
                          >
                            <Eye className="w-4 h-4" /> Lihat Detail
                          </button>
                          <button
                            className="w-full px-4 py-2 text-left hover:bg-gray-50 flex items-center gap-2 text-sm"
                            onClick={() => {
                              navigate(createPageUrl(`DebtorForm?id=${debtor.id}`));
                              setOpenMenu(null);
                            }}
                          >
                            <Edit className="w-4 h-4" /> Edit
                          </button>
                          <button
                            className="w-full px-4 py-2 text-left hover:bg-gray-50 flex items-center gap-2 text-sm"
                            onClick={() => {
                              navigate(
                                createPageUrl(`CreditApplicationForm?debtor_id=${debtor.id}`)
                              );
                              setOpenMenu(null);
                            }}
                          >
                            <FileText className="w-4 h-4" /> Buat Aplikasi Kredit
                          </button>
                          <button
                            className="w-full px-4 py-2 text-left hover:bg-gray-50 flex items-center gap-2 text-sm text-red-600"
                            onClick={() => {
                              onDelete(debtor.id);
                              setOpenMenu(null);
                            }}
                          >
                            <Trash2 className="w-4 h-4" /> Hapus
                          </button>
                        </div>
                      )}
                    </td>
                  </tr>
                );
              })
            )}
          </tbody>
        </table>
      </div>
    </div>
  );
}
